import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeSlashIcon,
  ArrowTopRightOnSquareIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { apiKeyApi, teamApi, mcpChecksApi } from '../lib/api';
import clsx from 'clsx';

interface ApiKey {
  id: string;
  name: string;
  keyPreview: string;
  teamId: string;
  team: { name: string };
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

interface McpStats {
  total: number;
  compliant: number;
  flagsFound: number;
  reviewRecommended: number;
  pendingReview: number;
  complianceRate: number;
}

export default function IntegrationsPage() {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Fetch teams
  const { data: teamsData } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => {
      const response = await teamApi.getTeams();
      return response.data;
    },
  });
  const teams = teamsData?.teams || teamsData || [];

  // Fetch API keys
  const { data: keysData, isLoading: keysLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const response = await apiKeyApi.listKeys();
      return response.data;
    },
  });
  const apiKeys: ApiKey[] = keysData?.apiKeys || [];

  // Fetch MCP stats
  const { data: statsData } = useQuery({
    queryKey: ['mcp-stats'],
    queryFn: async () => {
      const response = await mcpChecksApi.getStats({ period: 'last_30_days' });
      return response.data as McpStats;
    },
  });

  // Create key mutation
  const createKeyMutation = useMutation({
    mutationFn: (data: { name: string; teamId: string; scopes: string[] }) =>
      apiKeyApi.createKey(data),
    onSuccess: (response) => {
      const data = response.data;
      setNewKeyValue(data.key);
      setShowCreateForm(false);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key created!');
    },
    onError: () => {
      toast.error('Failed to create API key');
    },
  });

  // Revoke key mutation
  const revokeKeyMutation = useMutation({
    mutationFn: (id: string) => apiKeyApi.revokeKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key revoked');
    },
    onError: () => {
      toast.error('Failed to revoke API key');
    },
  });

  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    if (!selectedTeamId) {
      toast.error('Please select a team');
      return;
    }
    createKeyMutation.mutate({
      name: newKeyName.trim(),
      teamId: selectedTeamId,
      // Both rails by default: "mcp" (MCP server) + "api" (REST API v1).
      scopes: ['mcp', 'api'],
    });
  };

  const handleCopyKey = async () => {
    if (newKeyValue) {
      await navigator.clipboard.writeText(newKeyValue);
      setCopiedKey(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopiedKey(false), 3000);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Integrations</h1>
        <p className="mt-1 text-gray-500">
          Connect Clarid to Claude, ChatGPT, Microsoft Copilot, Perplexity, and any MCP-compatible AI platform via the Model Context Protocol.
        </p>
      </div>

      {/* MCP Server Connection Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-ocean-50 rounded-lg">
              <ServerIcon className="w-5 h-5 text-ocean-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-navy-900">MCP Server</h2>
              <p className="text-sm text-gray-500">Clarid for Claude, ChatGPT, Copilot, Perplexity &amp; more</p>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500" />
            Available
          </span>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Server URL</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value="https://api.clarid.ai/mcp"
                className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 font-mono"
              />
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText('https://api.clarid.ai/mcp');
                  toast.success('URL copied');
                }}
                className="p-2 text-gray-500 hover:text-ocean-600 transition-colors"
                title="Copy URL"
              >
                <ClipboardDocumentIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Authentication</label>
            <p className="text-sm text-gray-500">API Key (Bearer token) — generate below</p>
          </div>

          <div className="bg-ocean-50 border border-ocean-200 rounded-md p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-ocean-800 mb-2">Quick Setup (Any MCP Client)</h3>
              <ol className="text-sm text-ocean-700 space-y-1 list-decimal list-inside">
                <li>Generate an API key below</li>
                <li>Add the server URL and API key to your MCP client configuration</li>
                <li>Your plan's compliance tools (30+ across all tiers) are discovered automatically</li>
              </ol>
            </div>
            <div className="border-t border-ocean-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-ocean-800 mb-1">Microsoft Copilot</p>
                <p className="text-xs text-ocean-600">Copilot Studio &rarr; Agent &rarr; Tools &rarr; Add Tool &rarr; MCP</p>
                <a
                  href="https://copilotstudio.microsoft.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-1 text-xs font-medium text-ocean-600 hover:text-ocean-700"
                >
                  Open Copilot Studio
                  <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />
                </a>
              </div>
              <div>
                <p className="text-xs font-semibold text-ocean-800 mb-1">Claude Desktop</p>
                <p className="text-xs text-ocean-600">Settings &rarr; MCP Servers &rarr; Add Server &rarr; Streamable HTTP</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-ocean-800 mb-1">ChatGPT</p>
                <p className="text-xs text-ocean-600">Settings &rarr; Connected Tools &rarr; Add MCP Server</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-ocean-800 mb-1">Perplexity</p>
                <p className="text-xs text-ocean-600">Settings &rarr; MCP &rarr; Add Server URL</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Key Created Banner */}
      {newKeyValue && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800">Save Your API Key</h3>
              <p className="text-sm text-amber-700 mt-1">
                Copy this key now. You will not be able to see it again.
              </p>
              <div className="mt-3 flex items-center space-x-2">
                <code className="flex-1 bg-white border border-amber-300 rounded-md px-3 py-2 text-sm font-mono text-gray-800 break-all">
                  {newKeyValue}
                </code>
                <button
                  onClick={handleCopyKey}
                  className={clsx(
                    'flex-shrink-0 px-4 py-2 rounded-md text-sm font-medium transition-all',
                    copiedKey
                      ? 'bg-green-600 text-white'
                      : 'bg-amber-600 text-white hover:bg-amber-700'
                  )}
                >
                  {copiedKey ? (
                    <span className="flex items-center"><CheckCircleIcon className="w-4 h-4 mr-1" /> Copied</span>
                  ) : (
                    <span className="flex items-center"><ClipboardDocumentIcon className="w-4 h-4 mr-1" /> Copy</span>
                  )}
                </button>
              </div>
              <button
                onClick={() => setNewKeyValue(null)}
                className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                I've saved my key &mdash; dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <KeyIcon className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-navy-900">API Keys</h2>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="inline-flex items-center px-3 py-1.5 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 transition-all shadow-sm"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            New Key
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Copilot, Development Testing"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select a team...</option>
                  {(Array.isArray(teams) ? teams : []).map((team: any) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCreateKey}
                  disabled={createKeyMutation.isPending}
                  className="px-4 py-2 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 transition-all disabled:opacity-50"
                >
                  {createKeyMutation.isPending ? 'Creating...' : 'Create API Key'}
                </button>
                <button
                  onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Keys List */}
        <div className="divide-y divide-gray-200">
          {keysLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600" />
              <p className="text-gray-500 mt-4">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <EyeSlashIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">No API keys yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create an API key to connect Clarid to your AI assistant.
              </p>
            </div>
          ) : (
            apiKeys.map((key) => (
              <div key={key.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-navy-900 truncate">{key.name}</p>
                    {key.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 mt-1">
                    <code className="text-xs text-gray-500 font-mono">{key.keyPreview}</code>
                    <span className="text-xs text-gray-400">{key.team?.name}</span>
                    <span className="text-xs text-gray-400">
                      Last used: {formatDate(key.lastUsedAt)}
                    </span>
                    <span className="text-xs text-gray-400">
                      Created: {formatDate(key.createdAt)}
                    </span>
                  </div>
                </div>
                {key.isActive && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Revoke API key "${key.name}"? This action cannot be undone.`)) {
                        revokeKeyMutation.mutate(key.id);
                      }
                    }}
                    className="ml-4 p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Revoke key"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* MCP Activity Overview */}
      {statsData && statsData.total > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-navy-900">MCP Activity (Last 30 Days)</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-navy-900">{statsData.total}</p>
                <p className="text-sm text-gray-500">Total Checks</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{statsData.complianceRate}%</p>
                <p className="text-sm text-gray-500">Compliance Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{statsData.flagsFound}</p>
                <p className="text-sm text-gray-500">Flags Found</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{statsData.pendingReview}</p>
                <p className="text-sm text-gray-500">Pending Review</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Available Tools Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-navy-900">Available MCP Tools (10)</h2>
          <p className="text-sm text-gray-500 mt-1">
            These tools are automatically discovered by any MCP-compatible AI client when you connect the server.
          </p>
        </div>
        <div className="divide-y divide-gray-200">
          {[
            {
              name: 'check_compliance',
              description: 'General-purpose compliance check for marketing and advertising content.',
              regs: 'FDIC, NCUA, Reg DD, Reg Z, UDAAP, Fair Housing',
            },
            {
              name: 'check_email_compliance',
              description: 'Email-specific checks including CAN-SPAM, NMLS, and disclosure requirements.',
              regs: 'CAN-SPAM, S.A.F.E. Act, Reg DD/Z, UDAAP',
            },
            {
              name: 'check_social_compliance',
              description: 'Social media compliance with platform-specific rules and character limits.',
              regs: 'FDIC Social Media Guidance, UDAAP, Reg DD/Z',
            },
            {
              name: 'generate_disclosures',
              description: 'Proactively generates required disclosure language for any product and channel.',
              regs: 'Reg DD, Reg Z, FDIC Part 328, NCUA Part 740',
            },
            {
              name: 'compliance_summary',
              description: 'Query compliance activity, trends, and statistics conversationally.',
              regs: 'Audit trail reporting',
            },
            {
              name: 'fair_lending_screen',
              description: 'Screen content for fair lending issues — discriminatory language, steering indicators, and ECOA/FHA violations.',
              regs: 'ECOA / Reg B, Fair Housing Act, UDAAP',
            },
            {
              name: 'search_regulations',
              description: 'Search the regulatory knowledge base by query, agency, compliance area, or product type.',
              regs: 'All federal banking regulations',
            },
            {
              name: 'get_product_compliance',
              description: 'Retrieve the compliance profile for a banking product with mapped regulations and checklist status.',
              regs: 'Product-specific regulatory mappings',
            },
            {
              name: 'get_campaign_compliance',
              description: 'Get compliance status for a multi-channel marketing campaign with per-channel breakdown.',
              regs: 'Cross-channel compliance tracking',
            },
            {
              name: 'submit_feedback',
              description: 'Report an incorrect or inapplicable compliance flag to improve the rules engine.',
              regs: 'Rules engine feedback loop',
            },
          ].map((tool) => (
            <div key={tool.name} className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <code className="text-sm font-semibold text-ocean-700 bg-ocean-50 px-2 py-0.5 rounded">
                    {tool.name}
                  </code>
                  <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
                  <p className="text-xs text-gray-400 mt-1">Regulations: {tool.regs}</p>
                </div>
                <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
